// Section 17, 18 and 22 Rations tab, the tab root: reads the store's signals, builds the view
// models and dispatches the ration actions. Per-resource policy selectors, the food floor, the
// priority order editor with saved presets and the announce toggle, seed reserve, household
// sharing, ask-on-shortage, the override list, and an "affects next season" preview line.
// Every other component in this folder takes props. Edits are allowed only while deciding.
import { useComputed } from '@preact/signals';
import { useState } from 'preact/hooks';
import type {
  Config,
  FoodFloor,
  RationPolicyId,
  SeedReserveOption,
  WarmthOption,
  WaterRationOption,
} from '../../sim/api';
import {
  applySavedOrder,
  deleteSavedOrder,
  draft,
  game,
  pinRation,
  reorderPriority,
  savePriorityOrder,
  setAnnounce,
  setAskOnShortage,
  setFoodFloor,
  setHouseholdSharing,
  setMyHousehold,
  setRationPolicy,
  setSeedReserve,
  status,
} from '../../store';
import { jobLabel } from '../../viewModels/people';
import {
  FOOD_POLICIES,
  rationPreview,
  rationsVM,
  SEED_OPTIONS,
  WARMTH_POLICIES,
  WATER_POLICIES,
  type RationTabResource,
} from '../../viewModels/rations';
import {
  rationPolicyLabel,
  rationResourceLabel,
  rationsText,
  seedReserveLabel,
  warmthLabel,
  waterRationLabel,
} from '../text/en';
import { OverrideList } from './override-list';
import { PriorityEditor } from './priority-editor';
import './rations.css';

const RESOURCES: readonly RationTabResource[] = ['food', 'water', 'warmth'];

interface Props {
  /** The run's config when it is not the current one (a save's snapshot, ADR-0008). */
  cfg?: Config;
}

export function RationsTab({ cfg }: Props = {}) {
  const model = useComputed(() =>
    game.value === null ? null : rationsVM(game.value, draft.value),
  );
  const preview = useComputed(() =>
    game.value === null ? [] : rationPreview(game.value, draft.value, cfg),
  );
  const [pinsFor, setPinsFor] = useState<RationTabResource>('food');
  const [presetName, setPresetName] = useState('');
  const [presetAnnounced, setPresetAnnounced] = useState(true);
  const vm = model.value;
  if (vm === null) return null;
  const canEdit = status.value === 'deciding';
  const changed = (key: string): string | null =>
    (vm.changed as string[]).includes(key) ? rationsText.changed : null;

  return (
    <div class="rations">
      {!canEdit && <p class="hint rations__readonly">{rationsText.readOnly}</p>}

      <section class="rations__section" aria-labelledby="rations-preview-title">
        <h3 id="rations-preview-title">{rationsText.previewHeading}</h3>
        {preview.value.length === 0 ? (
          <p class="rations__preview">{rationsText.previewNone}</p>
        ) : (
          <ul class="rations__preview">
            {preview.value.map((p) => (
              <li key={p.resource}>
                {rationsText.preview(
                  rationResourceLabel[p.resource],
                  p.covered,
                  p.population,
                  p.short,
                  p.belowFloor,
                )}{' '}
                {p.willAsk && <strong>{rationsText.previewAsk}</strong>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section class="rations__section" aria-labelledby="rations-policies-title">
        <h3 id="rations-policies-title">{rationsText.policiesHeading}</h3>
        <div class="rations__grid">
          <label class="field">
            <span class="field__label">
              {rationsText.foodPolicy} <em class="hint">{changed('foodRation')}</em>
            </span>
            <select
              value={vm.foodRation}
              disabled={!canEdit}
              onChange={(e) => {
                setRationPolicy('food', e.currentTarget.value as RationPolicyId);
              }}
            >
              {FOOD_POLICIES.map((id) => (
                <option key={id} value={id}>
                  {rationPolicyLabel[id]}
                </option>
              ))}
            </select>
          </label>
          <label class="field">
            <span class="field__label">
              {rationsText.floor} <em class="hint">{changed('foodFloor')}</em>
            </span>
            <select
              value={String(vm.foodFloor)}
              disabled={!canEdit}
              onChange={(e) => {
                setFoodFloor(Number(e.currentTarget.value) as FoodFloor);
              }}
            >
              {vm.floorOptions.map((f) => (
                <option key={f} value={String(f)}>
                  {rationsText.floorOption(f)}
                </option>
              ))}
            </select>
          </label>
          <label class="field">
            <span class="field__label">
              {rationsText.waterPolicy} <em class="hint">{changed('waterRation')}</em>
            </span>
            <select
              value={vm.waterRation}
              disabled={!canEdit}
              onChange={(e) => {
                setRationPolicy('water', e.currentTarget.value as WaterRationOption);
              }}
            >
              {WATER_POLICIES.map((id) => (
                <option key={id} value={id}>
                  {waterRationLabel[id]}
                </option>
              ))}
            </select>
          </label>
          <label class="field">
            <span class="field__label">
              {rationsText.warmthPolicy} <em class="hint">{changed('warmth')}</em>
            </span>
            <select
              value={vm.warmth}
              disabled={!canEdit}
              onChange={(e) => {
                setRationPolicy('warmth', e.currentTarget.value as WarmthOption);
              }}
            >
              {WARMTH_POLICIES.map((id) => (
                <option key={id} value={id}>
                  {warmthLabel[id]}
                </option>
              ))}
            </select>
          </label>
          <label class="field">
            <span class="field__label">
              {rationsText.seed} <em class="hint">{changed('seedReserve')}</em>
            </span>
            <select
              value={vm.seedReserve}
              disabled={!canEdit}
              onChange={(e) => {
                setSeedReserve(e.currentTarget.value as SeedReserveOption);
              }}
            >
              {SEED_OPTIONS.map((id) => (
                <option key={id} value={id}>
                  {seedReserveLabel[id]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p class="hint">{rationsText.floorHint}</p>
        <div class="rations__toggles">
          <label class="field field--check">
            <input
              type="checkbox"
              checked={vm.householdSharing}
              disabled={!canEdit}
              onChange={(e) => {
                setHouseholdSharing(e.currentTarget.checked);
              }}
            />
            <span>
              {rationsText.sharing} <span class="hint">{rationsText.sharingHint}</span>
            </span>
          </label>
          <label class="field field--check">
            <input
              type="checkbox"
              checked={vm.askOnShortage}
              disabled={!canEdit}
              onChange={(e) => {
                setAskOnShortage(e.currentTarget.checked);
              }}
            />
            <span>
              {rationsText.ask} <span class="hint">{rationsText.askHint}</span>
            </span>
          </label>
        </div>
      </section>

      <section class="rations__section" aria-labelledby="rations-order-title">
        <h3 id="rations-order-title">
          {rationsText.orderHeading} <em class="hint">{changed('foodPriority')}</em>
        </h3>
        <label class="field field--check rations__announce">
          <input
            type="checkbox"
            checked={vm.announce}
            disabled={!canEdit}
            onChange={(e) => {
              setAnnounce(e.currentTarget.checked);
            }}
          />
          <span>
            {rationsText.announce} <span class="hint">{rationsText.announceHint}</span>
          </span>
        </label>
        <PriorityEditor
          order={vm.order}
          unused={vm.unused}
          canEdit={canEdit}
          onReorder={(groups) => reorderPriority(groups)}
        />
        <h4 class="rations__subhead">{rationsText.presetsHeading}</h4>
        {vm.savedOrders.length === 0 ? (
          <p class="hint">{rationsText.noPresets}</p>
        ) : (
          <ul class="rations__presets">
            {vm.savedOrders.map((o) => (
              <li key={o.id}>
                <span class="name">{o.name}</span>{' '}
                <span class="hint">
                  ({o.announced ? rationsText.announcedTag : rationsText.hiddenTag})
                </span>
                <button
                  type="button"
                  class="button button--small"
                  disabled={!canEdit}
                  onClick={() => applySavedOrder(o.id)}
                >
                  {rationsText.applyOrder}
                </button>
                <button
                  type="button"
                  class="button button--small"
                  disabled={!canEdit}
                  onClick={() => deleteSavedOrder(o.id)}
                >
                  {rationsText.deleteOrder}
                </button>
              </li>
            ))}
          </ul>
        )}
        <form
          class="rations__save"
          onSubmit={(e) => {
            e.preventDefault();
            if (presetName.trim() === '') return;
            savePriorityOrder(presetName, presetAnnounced);
            setPresetName('');
          }}
        >
          <label class="field">
            <span class="field__label">{rationsText.presetName}</span>
            <input
              type="text"
              value={presetName}
              maxLength={40}
              disabled={!canEdit}
              onInput={(e) => {
                setPresetName(e.currentTarget.value);
              }}
            />
          </label>
          <label class="field field--check">
            <input
              type="checkbox"
              checked={presetAnnounced}
              disabled={!canEdit}
              onChange={(e) => {
                setPresetAnnounced(e.currentTarget.checked);
              }}
            />
            <span>{rationsText.presetAnnounced}</span>
          </label>
          <button type="submit" class="button" disabled={!canEdit || presetName.trim() === ''}>
            {rationsText.saveOrder}
          </button>
        </form>
      </section>

      <section class="rations__section" aria-labelledby="rations-overrides-title">
        <h3 id="rations-overrides-title">
          {rationsText.overridesHeading} <em class="hint">{changed('pins')}</em>
        </h3>
        <div class="rations__tabs" role="group" aria-label={rationsText.overridesFor}>
          {RESOURCES.map((r) => (
            <button
              key={r}
              type="button"
              class={`button button--small${pinsFor === r ? ' is-selected' : ''}`}
              aria-pressed={pinsFor === r}
              onClick={() => {
                setPinsFor(r);
              }}
            >
              {rationResourceLabel[r]}
            </button>
          ))}
        </div>
        <OverrideList
          resource={pinsFor}
          people={vm.people}
          households={vm.households}
          canEdit={canEdit}
          jobLabel={jobLabel}
          onPin={(target, share) => pinRation(target, share, pinsFor)}
          onMyHousehold={(id, member) =>
            setMyHousehold(
              member ? [...vm.myHousehold, id] : vm.myHousehold.filter((x) => x !== id),
            )
          }
        />
      </section>
    </div>
  );
}
